import { scaleLinear } from 'd3'
import type { SwingTimingData } from 'leaderboard/scripts/swing-path/types/globals.ts'
import SimpleChart, {
    CurveInfo,
    ScaleType,
    UserRequiredOptions,
} from '../visuals/simple-chart.ts'
import type { ProcessedPitchData } from './process-pitch-data.ts'
import {
    asHandedness,
    getLeagueCurveKey,
    getLeagueGroupKey,
    isLeagueKey,
    LEAGUE_PREFIX,
    parseUniqueId,
} from './key-parsing.ts'
import {
    getPitchTypeColor,
    getPitchTypeColorGrouping,
} from '../utils/pitch-type-colors'
import {
    DistributionObj,
    getKeyFromRow,
    HandednessWithCombination,
} from './Wrapper.tsx'
import processPitchData from './process-pitch-data.ts'
import getDistributionColor, {
    removeColorByKey,
    removeColorsByPrefix,
} from './utils/get-distribution-color.ts'
import {
    getPitchTypeColorModeByUniqueId,
    type PitchTypeColorMode,
} from './utils/can-use-pitch-type-colors.ts'

export type Dimension = 'x' | 'y' | 'z'

type SwingTimingChartOptions = UserRequiredOptions & {
    centeredInterval: [number, number]
    dim: Dimension
}

type TextSelection = d3.Selection<
    SVGTextElement,
    unknown,
    HTMLElement,
    undefined
>

const fontSizeScale = scaleLinear()
    .domain([300, 500])
    .range([0.75, 1])
    .clamp(true)

const metaData = {
    x: {
        buckets: {
            maxBucket: 15,
            minBucket: -12,
        },
        xTickValues: [-12, -6, 0, 6, 12],
        centeredInterval: [-4, 4] as [number, number],
        axisInfo: {
            // xAxisTitle: 'Tied Up / Flail',
            // yAxisTitle: 'Swings',
            xAxisTitle: '',
            yAxisTitle: '',
        },
        bucketSize: 2,
        images: [
            'swings-tiedup.png',
            'swings-centered.png',
            'swings-flail.png',
        ],
    },
    y: {
        buckets: {
            maxBucket: 30,
            minBucket: -30,
        },
        xTickValues: [-20, -10, 0, 10, 20],
        centeredInterval: [-7, 7] as [number, number],
        axisInfo: {
            // xAxisTitle: 'Late / Early',
            xAxisTitle: '',
            yAxisTitle: '',
        },
        bucketSize: 2,
        images: ['swings-late.png', 'swings-ontime.png', 'swings-early.png'],
    },
    z: {
        buckets: {
            maxBucket: 10,
            minBucket: -10,
        },
        xTickValues: [-6, 0, 6],
        centeredInterval: [-2, 2] as [number, number],
        axisInfo: {
            // xAxisTitle: 'Over / Under',
            xAxisTitle: '',
            yAxisTitle: '',
        },
        bucketSize: 1,
        images: ['swings-over.png', 'swings-linedup.png', 'swings-under.png'],
    },
}

const heights = [400, 400, 400, 400]

const scaleType: 'n' | 'n_pct' = 'n_pct'

function getYAxisTitleForScale(currentScaleType: ScaleType): string {
    return currentScaleType === 'n' ? 'Swings' : 'Swing Percent'
}

const sides = ['L', 'R'] as const satisfies [
    HandednessWithCombination,
    HandednessWithCombination
]

export default class SwingTimingChart extends SimpleChart<SwingTimingChartOptions> {
    xAxisTitleSwingTiming: d3.Selection<
        SVGGElement,
        unknown,
        HTMLElement,
        undefined
    >

    xAxisTitleSwingTimingLeft: TextSelection

    xAxisTitleSwingTimingCenter: TextSelection

    xAxisTitleSwingTimingCenterUnits: TextSelection

    xAxisTitleSwingTimingRight: TextSelection

    isMerged = true

    showLeagueData: boolean = false

    leagueData: DistributionObj

    dim: Dimension

    idx: number

    rect: d3.Selection<SVGRectElement, unknown, HTMLElement, undefined>

    store: Record<string, DistributionObj> = {}

    fetchingData: boolean = false

    usePitchTypeColors: boolean = false

    pitchTypeColorMode: PitchTypeColorMode = 'none'

    groupBys: string[]

    onCurveHover: ((key: string | null) => void) | null = null

    implicitHandedness: Record<
        string,
        {
            pitchHand: HandednessWithCombination | null
            batSide: HandednessWithCombination | null
        }
    > = {}

    parseDataKey(key: string): {
        pitchType03: string | null
        pitchType09: string | null
        pitchHand: HandednessWithCombination | null
        batSide: HandednessWithCombination | null
        year: number | null
    } {
        if (isLeagueKey(key)) {
            return this.parseDataKey(key.slice(LEAGUE_PREFIX.length))
        }

        const unique = parseUniqueId(key, this.groupBys)
        return {
            pitchType03:
                typeof unique.api_pitch_type_group03 === 'string'
                    ? unique.api_pitch_type_group03
                    : null,
            pitchType09:
                typeof unique.api_pitch_type_group09 === 'string'
                    ? unique.api_pitch_type_group09
                    : null,
            pitchHand: asHandedness(
                unique.pitch_hand ?? unique.pitch_hand_type
            ),
            batSide: asHandedness(unique.bat_side ?? unique.bat_side_type),
            year: typeof unique.year === 'number' ? unique.year : null,
        }
    }

    getPitchTypeForMode(parsed: {
        pitchType03: string | null
        pitchType09: string | null
    }): string | null {
        if (this.pitchTypeColorMode === 'group09') {
            return parsed.pitchType09
        }

        if (this.pitchTypeColorMode === 'group03') {
            return parsed.pitchType03
        }

        return parsed.pitchType03 ?? parsed.pitchType09
    }

    getPitchTypeFilterMode(): 'group03' | 'group09' {
        return this.groupBys.includes('api_pitch_type_group09')
            ? 'group09'
            : 'group03'
    }

    getPitchTypeForGrouping(parsed: {
        pitchType03: string | null
        pitchType09: string | null
    }): string | null {
        if (this.getPitchTypeFilterMode() === 'group09') {
            return parsed.pitchType09 ?? parsed.pitchType03
        }

        return parsed.pitchType03 ?? parsed.pitchType09
    }

    buildAllPlayerCurves(keys: string[]) {
        const newColorMode = this.getPitchTypeColorMode(keys)
        const modeChanged = newColorMode !== this.pitchTypeColorMode

        this.pitchTypeColorMode = newColorMode
        this.usePitchTypeColors = this.pitchTypeColorMode !== 'none'

        if (modeChanged && this.usePitchTypeColors) {
            keys.forEach((key) => removeColorByKey(key))
        }

        return keys.flatMap((key) => {
            const dataObj = this.store[key]

            if (!dataObj) {
                return []
            }

            const parsed = this.parseDataKey(key)
            const pitchType = this.getPitchTypeForMode(parsed)
            return [this.buildPlayerCurve(dataObj, key, pitchType)]
        })
    }

    buildPlayerCurve(
        dataObj: DistributionObj,
        key: string,
        pitchType: string | null
    ): CurveInfo {
        const data = dataObj[this.options.dim]
        const total = data.reduce((acc, curr) => acc + curr.n, 0)

        const dataWithPct = data.map((b) => ({
            ...b,
            n_pct: total > 0 ? b.n / total : 0,
        }))

        let color = getDistributionColor(key)

        if (this.usePitchTypeColors && pitchType !== null) {
            color =
                this.pitchTypeColorMode === 'group09'
                    ? getPitchTypeColorGrouping(pitchType, false)
                    : getPitchTypeColor(pitchType, false)
        }

        return {
            color,
            data: dataWithPct,
            key,
        }
    }

    // interesting AI choice (static)
    static filterDistributionByPitchType(
        dataObj: DistributionObj,
        pitchType: string,
        pitchTypeColorMode: PitchTypeColorMode
    ): DistributionObj {
        const pitchTypeField =
            pitchTypeColorMode === 'group09'
                ? 'api_pitch_type_group09'
                : 'api_pitch_type_group03'

        const isMatch = (d: DistributionObj['x'][number]) =>
            d[pitchTypeField] === pitchType

        return {
            x: dataObj.x.filter(isMatch),
            y: dataObj.y.filter(isMatch),
            z: dataObj.z.filter(isMatch),
        }
    }

    constructor(
        el: d3.Selection<SVGSVGElement, unknown, HTMLElement, undefined>,
        dim: Dimension,
        idx: number,
        leagueData: DistributionObj,
        groupBys: string[]
    ) {
        const { axisInfo, buckets, bucketSize, centeredInterval, xTickValues } =
            metaData[dim]

        axisInfo.yAxisTitle = getYAxisTitleForScale(scaleType)
        const options = {
            ...buckets,
            axisInfo,
            bucketSize,
            centeredInterval,
            height: heights[idx],
            scaleType,
            xTickValues,
            dim,
            i: idx,
        }

        // Initialize without data
        super(el, [], options)

        this.dim = dim
        this.idx = idx

        this.rect = this.curvesWrapper.append('rect')

        this.placeRect(false)

        this.leagueData = leagueData
        this.groupBys = groupBys

        const fontSizeRems = fontSizeScale(this.dims.chartWidth)

        this.xAxisTitleSwingTiming = this.chart
            .append('g')
            .attr('class', 'x-label-swing-timing')
            .style('text-anchor', 'middle')
            .style('font-size', `${fontSizeRems}rem`)
            .style('text-transform', `uppercase`)

        this.xAxisTitleSwingTimingLeft =
            this.xAxisTitleSwingTiming.append('text')

        this.xAxisTitleSwingTimingCenter =
            this.xAxisTitleSwingTiming.append('text')

        this.xAxisTitleSwingTimingCenterUnits = this.xAxisTitleSwingTiming
            .append('text')
            .style('font-size', '0.85em')
            .style('text-transform', 'none')

        this.xAxisTitleSwingTimingRight =
            this.xAxisTitleSwingTiming.append('text')

        this.updateXLabels()

        this.placeHelperImages()
        this.placeSavantLogo()
    }

    placeSavantLogo() {
        const logoSize = 28
        const padding = 6
        const opacity = 0.5

        this.svg
            .append('image')
            .attr('class', 'savant-logo-image')
            .attr('width', logoSize)
            .attr('height', logoSize)
            .attr(
                'x',
                this.dims.margin.left +
                    this.dims.chartWidth -
                    logoSize -
                    padding
            )
            .attr('y', this.dims.margin.top + padding)
            .attr('xlink:href', '/site-core/images/savant-logo.svg')
            .attr('opacity', opacity)

        this.svg
            .append('text')
            .attr('class', 'savant-logo-text')
            .text('savant')
            .attr(
                'x',
                this.dims.margin.left +
                    this.dims.chartWidth -
                    logoSize / 2 -
                    padding
            )
            .attr('y', this.dims.margin.top + padding + logoSize + 10)
            .attr('text-anchor', 'middle')
            .attr('font-size', '12px')
            .attr('fill', '#399098')
            .attr('opacity', opacity)
    }

    placeHelperImages() {
        const {
            minBucket,
            maxBucket,
            centeredInterval: [min, max],
        } = this.options

        const nudgeUpFactor = 20
        const helperImages = this.svg
            .append('g')
            .attr(
                'transform',
                `translate(${this.dims.margin.left},${
                    this.dims.height - nudgeUpFactor
                })`
            )

        const size = this.dims.chartWidth / 3
        const { images } = metaData[this.dim]
        const leftX = this.xScale((min + minBucket) / 2)
        const middleX = this.xScale(0)
        const rightX = this.xScale((max + maxBucket) / 2)

        const xDims = [leftX, middleX, rightX]
        if (images) {
            images.forEach((fileName, i) => {
                const centerX = xDims[i]
                const x = centerX - size / 2
                helperImages
                    .append('image')
                    .attr('x', x)
                    .attr('y', 0)
                    .attr('width', size)
                    .attr('height', size)
                    .attr('preserveAspectRatio', 'none')
                    .attr(
                        'xlink:href',
                        `/site-core/images/swing-timing/${fileName}`
                    )
            })
        }
    }

    addData(data: CurveInfo) {
        this.rawData = this.rawData.filter((d) => !isLeagueKey(d.key))
        return super.addData(data)
    }

    addRawerData(rawData: DistributionObj, row: SwingTimingData) {
        const key = getKeyFromRow(row)

        const keys = this.rawData.map((d) => d.key)
        if (keys.includes(key)) {
            // Run an update?
            throw new Error('duplicate key')
        }

        this.store[key] = rawData
        this.implicitHandedness[key] = {
            pitchHand: asHandedness(row.pitch_hand_type),
            batSide: asHandedness(row.bat_side_type),
        }

        const keysForNextUpdate = [
            ...this.rawData
                .filter((d) => !isLeagueKey(d.key))
                .map((d) => d.key),
            key,
        ]
        const data = this.buildAllPlayerCurves(keysForNextUpdate)
        this.updateData(data)
    }

    placeRect(isTransition = true) {
        const [min, max] = this.options.centeredInterval
        const attrs = {
            fill: '#ddd',
            height: this.dims.chartHeight - 2,
            width: this.xScale(max) - this.xScale(min),
            x: this.xScale(min),
            y: 1,
        }

        // Using if/else because TypeScript can't narrow Selection | Transition
        // union type from a ternary — it loses track of the common .attr() method
        if (isTransition) {
            this.rect
                .transition()
                .duration(378)
                .attr('fill', attrs.fill)
                .attr('height', attrs.height)
                .attr('width', attrs.width)
                .attr('x', attrs.x)
                .attr('y', attrs.y)
        } else {
            this.rect
                .attr('fill', attrs.fill)
                .attr('height', attrs.height)
                .attr('width', attrs.width)
                .attr('x', attrs.x)
                .attr('y', attrs.y)
        }

        this.rect.lower()

        this.background.lower()
    }

    updateXLabels() {
        const {
            minBucket,
            maxBucket,
            centeredInterval: [min, max],
        } = this.options

        this.xAxisTitleSwingTiming.attr(
            'transform',
            `translate(0 ,${this.dims.chartHeight + 28})`
        )

        this.xAxisTitleSwingTimingLeft.attr(
            'x',
            this.xScale((min + minBucket) / 2)
        )
        const textHeight = 18
        this.xAxisTitleSwingTimingLeft.attr('y', textHeight)

        this.xAxisTitleSwingTimingCenter.attr('x', this.xScale(0))
        this.xAxisTitleSwingTimingCenter.attr('y', textHeight)
        this.xAxisTitleSwingTimingRight.attr(
            'x',
            this.xScale((max + maxBucket) / 2)
        )
        this.xAxisTitleSwingTimingRight.attr('y', textHeight)

        this.xAxisTitleSwingTimingCenterUnits
            .attr('x', this.xScale(0))
            .attr('y', 0)

        if (this.options.dim === 'x') {
            this.xAxisTitleSwingTimingLeft.text('← Tied Up')
            this.xAxisTitleSwingTimingCenter.text('Centered')
            this.xAxisTitleSwingTimingCenterUnits.text('inches')
            this.xAxisTitleSwingTimingRight.text('Flail →')
        } else if (this.options.dim === 'y') {
            this.xAxisTitleSwingTimingLeft.text('← Late')
            this.xAxisTitleSwingTimingCenter.text('On Time')
            this.xAxisTitleSwingTimingCenterUnits.text('milliseconds')
            this.xAxisTitleSwingTimingRight.text('Early →')
        } else if (this.options.dim === 'z') {
            this.xAxisTitleSwingTimingLeft.text('← Over')
            this.xAxisTitleSwingTimingCenter.text('Lined Up')
            this.xAxisTitleSwingTimingCenterUnits.text('inches')
            this.xAxisTitleSwingTimingRight.text('Under →')
        }
    }

    updateDim(newDim: Dimension) {
        // Update Options
        this.options.dim = newDim
        // Do I need this?
        this.dim = newDim

        const { axisInfo, buckets, bucketSize, centeredInterval, xTickValues } =
            metaData[newDim]

        axisInfo.yAxisTitle = getYAxisTitleForScale(this.scaleType)
        const options = {
            ...buckets,
            axisInfo,
            bucketSize,
            centeredInterval,
            xTickValues,
            height: heights[this.idx],
            scaleType,
            dim: this.options.dim,
            i: this.idx,
        }

        this.options = { ...this.defaultOptions, ...options }

        // Update data
        const newRawData = this.rawData.flatMap((d) => {
            const baseKey = `${d.key.slice(0, d.key.length - 1)}X`
            const dataObj = this.store[baseKey] ?? this.store[d.key]

            if (!dataObj) {
                return []
            }

            const parsed = this.parseDataKey(d.key)
            const pitchType = this.getPitchTypeForMode(parsed)
            return [this.buildPlayerCurve(dataObj, d.key, pitchType)]
        })

        this.updateScales()

        this.placeRect()
        this.updateAxes()
        this.updateXLabels()

        this.updateData(newRawData)
    }

    // For PlayerPages
    updatePlayerYear(
        newPlayerData: DistributionObj,
        leagueData: DistributionObj
    ) {
        this.leagueData = leagueData

        // Update data: rebuild per-pitch player curves using stable keys.
        const newRawData = this.rawData
            .filter((d) => !isLeagueKey(d.key))
            .flatMap((d) => {
                const parsed = this.parseDataKey(d.key)
                const pitchType = this.getPitchTypeForGrouping(parsed)

                if (!pitchType) {
                    return []
                }

                const pitchData =
                    SwingTimingChart.filterDistributionByPitchType(
                        newPlayerData,
                        pitchType,
                        this.getPitchTypeFilterMode()
                    )

                if (!pitchData[this.options.dim].length) {
                    removeColorByKey(d.key)
                    delete this.store[d.key]
                    delete this.implicitHandedness[d.key]
                    return []
                }

                this.store[d.key] = pitchData

                return this.buildPlayerCurve(pitchData, d.key, pitchType)
            })

        this.updateScales()

        this.updateData(newRawData)
    }

    async splitCurves(keys: string[], splitData: [CurveInfo, CurveInfo][]) {
        const rawData: CurveInfo[] = keys.flatMap((key, i) => {
            const currentData = this.rawData.find((d) => d.key === key)
            const [data1, data2] = splitData[i]

            if (!currentData) {
                throw new Error('Could not find current data')
            }

            const data1Init = { ...currentData, key: data1.key, split: true }
            const data2Init = { ...currentData, key: data2.key, split: true }

            return [data1Init, data2Init]
        })

        await this.updateData(rawData, false)

        const realRawData = splitData.flat()

        return this.updateData(realRawData, true)
    }

    async mergeMultipleCurves(
        newData: {
            data: ProcessedPitchData
            oldKeys: [string, string]
            newKey: string
        }[]
    ) {
        const unmergedRawData = newData.flatMap(({ data, oldKeys }) => {
            const [key1, key2] = oldKeys

            const newCurve1 = { ...data, key: key1, split: true }
            const newCurve2 = { ...data, key: key2, split: true }

            return [newCurve1, newCurve2]
        })

        await this.updateData(unmergedRawData)

        const mergedData = newData.map((d) => d.data)

        return this.updateData(mergedData, false)
    }

    getPitchTypeColorMode(keys: string[]): PitchTypeColorMode {
        return getPitchTypeColorModeByUniqueId(keys, this.groupBys)
    }

    async updateData(rawData: CurveInfo[], transition = true) {
        const data = rawData.filter((d) => !isLeagueKey(d.key))
        let averageData: CurveInfo[] = []

        if (this.showLeagueData && this.scaleType === 'n_pct') {
            const { dim } = this.options
            const deduped = new Map<
                string,
                {
                    pitchType: string | null
                    pitchHand: HandednessWithCombination | null
                    batSide: HandednessWithCombination | null
                    year: number | null
                }
            >()

            data.forEach((d) => {
                const parsed = this.parseDataKey(d.key)
                const implicit = this.implicitHandedness[d.key]
                const pitchType = this.getPitchTypeForGrouping(parsed)
                const merged = {
                    pitchType,
                    pitchHand: implicit?.pitchHand ?? null,
                    batSide: implicit?.batSide ?? null,
                    year: parsed.year,
                }
                const groupKey = getLeagueGroupKey(merged)

                if (!deduped.has(groupKey)) {
                    deduped.set(groupKey, merged)
                }
            })

            averageData = [...deduped.values()].map((parsed) => {
                const key = getLeagueCurveKey(parsed)
                return processPitchData(
                    this.leagueData,
                    dim,
                    parsed.pitchType,
                    parsed.pitchHand,
                    parsed.batSide,
                    key,
                    this.pitchTypeColorMode,
                    true,
                    parsed.year,
                    this.getPitchTypeFilterMode()
                )
            })
        }
        const result = await super.updateData(
            [...data, ...averageData],
            transition
        )
        this.attachCurveHoverHandlers()
        return result
    }

    updateScaleType(newScaleType: ScaleType): Promise<boolean> {
        if (newScaleType === 'n') {
            this.showLeagueData = false
            this.rawData = this.rawData.filter((d) => !isLeagueKey(d.key))
            removeColorsByPrefix(LEAGUE_PREFIX)
        }

        this.updateYAxisTitle(getYAxisTitleForScale(newScaleType))
        return super.updateScaleType(newScaleType)
    }

    toggleLeagueAverages() {
        this.showLeagueData = !this.showLeagueData

        if (!this.showLeagueData) {
            removeColorsByPrefix(LEAGUE_PREFIX)
        }

        return this.updateData(this.rawData)
    }

    setLeagueAverageStatus(show: boolean) {
        this.showLeagueData = show

        if (!show) {
            removeColorsByPrefix(LEAGUE_PREFIX)
        }

        this.updateData(this.rawData)
    }

    removeByRow(row: SwingTimingData) {
        const key = getKeyFromRow(row)
        // Feels weird
        removeColorByKey(key)

        delete this.store[key]
        delete this.implicitHandedness[key]

        const keysForNextUpdate = Object.keys(this.store).filter(
            (k) => !isLeagueKey(k)
        )

        const data = this.buildAllPlayerCurves(keysForNextUpdate)
        this.updateData(data)
    }

    handleSplit() {
        const rawData = Object.entries(this.store)

        const splits = rawData.map(([key, d]) => {
            const parsed = this.parseDataKey(key)
            const pitchType = this.getPitchTypeForGrouping(parsed)
            const { pitchHand } = parsed
            const baseKey = key.slice(0, key.length - 1)

            const [sideA, sideB] = sides
            return [
                processPitchData(
                    d,
                    this.dim,
                    pitchType,
                    pitchHand,
                    sideA,
                    baseKey + sideA,
                    this.pitchTypeColorMode,
                    false,
                    null,
                    this.getPitchTypeFilterMode()
                ),
                processPitchData(
                    d,
                    this.dim,
                    pitchType,
                    pitchHand,
                    sideB,
                    baseKey + sideB,
                    this.pitchTypeColorMode,
                    false,
                    null,
                    this.getPitchTypeFilterMode()
                ),
            ] as [CurveInfo, CurveInfo]
        })

        return this.splitCurves(
            rawData.map(([key]) => key),
            splits
        )
    }

    handleMerge() {
        const rawData = Object.entries(this.store)

        const newData = rawData.map(([key, d]) => {
            const baseKey = key.slice(0, key.length - 1)
            const parsed = this.parseDataKey(key)
            const pitchType = this.getPitchTypeForGrouping(parsed)
            const { pitchHand } = parsed
            const data = processPitchData(
                d,
                this.dim,
                pitchType,
                pitchHand,
                'X',
                key,
                this.pitchTypeColorMode,
                false,
                null,
                this.getPitchTypeFilterMode()
            )

            return {
                data,
                oldKeys: [`${baseKey}L`, `${baseKey}R`] as [string, string],
                newKey: key,
            }
        })

        return this.mergeMultipleCurves(newData)
    }

    async handleSplitMerge() {
        if (this.isMerged) {
            await this.handleSplit()
        } else {
            await this.handleMerge()
        }
        this.isMerged = !this.isMerged
    }

    setOnCurveHover(callback: ((key: string | null) => void) | null) {
        this.onCurveHover = callback
    }

    attachCurveHoverHandlers() {
        this.curvesWrapper
            .selectAll<SVGGElement, CurveInfo>('g.curve')
            .on('mouseover', (event, d) => {
                this.setHoveredCurve(d.key)

                if (this.onCurveHover && !isLeagueKey(d.key)) {
                    this.onCurveHover(d.key)
                }
            })
            .on('mouseout', () => {
                this.setHoveredCurve(null)

                if (this.onCurveHover) {
                    this.onCurveHover(null)
                }
            })
    }
}
